/**
 * Pure rules behind the BrandItem boost UI: what counts as a running boost,
 * what an item's price becomes while boosted, and why the Set button is
 * disabled. Free of React, Apollo and MUI so the rules the operator sees can
 * be unit tested without rendering anything.
 */
import {BrandItemBoostFieldsFragment, EnumBrandItemItemState} from "../../Queries";

/** Core accepts whole percentages in this range only. */
export const MIN_BOOST_PCT = 1;
export const MAX_BOOST_PCT = 99;

/** The parts of a boost the rules below care about. */
export type BoostLike = Pick<BrandItemBoostFieldsFragment, 'active' | 'pricePct' | 'visibility'>

/** The parts of an item that decide whether Core will accept a boost on it. */
export type BoostableItem = {
   itemState?: EnumBrandItemItemState | null
   discarded?: Date | null
   soldTs?: Date | null
}

/**
 * Dialog form state. `pricePct` is a fraction because that is how
 * FormInputPercent stores it - 0.2 renders as "20%".
 */
export type BoostFormValues = {
   discountEnabled: boolean
   pricePct: number | null
   visibility: boolean
   endTs: string | Date | null
}

/**
 * Convert FormInputPercent's fraction into the whole percentage Core expects.
 * The input allows two decimals, so 0.205 rounds to 21 rather than being
 * rejected as a non-integer.
 */
export const toWholePercent = (fraction?: number | null): number | null => {
   if (fraction === null || fraction === undefined || !isFinite(fraction)) {
      return null;
   }
   return Math.round(fraction * 100);
}

/**
 * BrandItem.activeBoost holds only the boost in effect right now, so its
 * presence is usually answer enough. `active` is still checked, so an entry
 * taken from `boosts` or from a stale cache never reads as running.
 */
export const isBoostRunning = (boost?: BoostLike | null): boolean => {
   return !!boost?.active;
}

/** The item's price while a boost of `wholePercent` runs, rounded to cents. */
export const boostedPrice = (price: number, wholePercent: number): number => {
   return Math.round(price * (100 - wholePercent)) / 100;
}

/**
 * Why Core would refuse a boost on this item, or undefined when it would
 * accept one. Mirrors Core's own messages so operators only ever read one
 * vocabulary; Core stays the authority and its errors are still surfaced.
 */
export const boostRefusalReason = (item: BoostableItem): string | undefined => {
   if (item.soldTs) {
      return 'Cannot boost a sold item';
   }
   if (item.discarded) {
      return 'Cannot boost a discarded item';
   }
   if (item.itemState !== EnumBrandItemItemState.READY) {
      return 'Only READY items can be boosted';
   }
   return undefined;
}

/**
 * Why the form cannot be submitted yet, or undefined when it can. Checks the
 * three rules Core enforces, so a valid-looking form never round-trips only to
 * come back with an error.
 */
export const validateBoostForm = (values: BoostFormValues, now: Date = new Date()): string | undefined => {
   if (!values.discountEnabled && !values.visibility) {
      return 'A boost must enable at least one of price or visibility';
   }
   if (values.discountEnabled) {
      const pct = toWholePercent(values.pricePct);
      if (pct === null || pct < MIN_BOOST_PCT || pct > MAX_BOOST_PCT) {
         return `Unsupported boost percentage: ${pct ?? '-'}`;
      }
   }
   if (!values.endTs) {
      return 'Choose when the boost should end';
   }
   if (new Date(values.endTs).getTime() <= now.getTime()) {
      return 'Boost end time must be in the future';
   }
   return undefined;
}

/** How many of the given items already have a boost running. */
export const countRunningBoosts = (items: {activeBoost?: BoostLike | null}[]): number => {
   return items.filter(item => isBoostRunning(item.activeBoost)).length;
}
